import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, AlertCircle, Eye, EyeOff } from 'lucide-react';

export default function RegisterPage() {
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => { document.title = 'Register | BrainShift AI Workspace'; }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(form);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#F0F4FA]">
        <img
          src="/brainshift-bg.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="flex items-center gap-3 mb-6">
            <img src="/brainshift-logo.png" alt="BrainShift" className="w-auto" style={{ height: '4.75rem' }} />
          </div>
          <p className="text-[#374151] text-lg max-w-md leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
            Upload documents, ask questions with AI-cited answers, and run compliance agents across your knowledge base.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-2 lg:hidden">
            <img src="/brainshift-logo.png" alt="BrainShift" className="w-auto" style={{ height: '4.75rem' }} />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#111827] mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            Create account
          </h1>
          <p className="text-[#4B5563] mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
            Get started with BrainShift
          </p>

          {error && (
            <div className="flex items-center gap-2 p-3 mb-6 border border-[#C8102E]/30 bg-[#C8102E]/5 text-[#C8102E] text-sm rounded-xl" data-testid="register-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="reg-first-name" className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2 block">First Name</Label>
                <Input
                  id="reg-first-name"
                  name="given-name"
                  autoComplete="given-name"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  required
                  className="rounded-xl border-[#E5E7EB] focus:ring-2 focus:ring-[#6590FF] h-11"
                  placeholder="Jane"
                  data-testid="register-first-name"
                />
              </div>
              <div>
                <Label htmlFor="reg-last-name" className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2 block">Last Name</Label>
                <Input
                  id="reg-last-name"
                  name="family-name"
                  autoComplete="family-name"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  required
                  className="rounded-xl border-[#E5E7EB] focus:ring-2 focus:ring-[#6590FF] h-11"
                  placeholder="Doe"
                  data-testid="register-last-name"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="reg-email" className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2 block">Email</Label>
              <Input
                id="reg-email"
                name="email"
                type="email"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="rounded-xl border-[#E5E7EB] focus:ring-2 focus:ring-[#6590FF] h-11"
                placeholder="jane@company.com"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label htmlFor="reg-password" className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2 block">Password</Label>
              {/* Same secure-input pattern as login.  Signup uses `autocomplete="new-password"`
                  so the browser offers to generate and save a strong password instead of
                  auto-filling a previously saved one. */}
              <div className="relative">
                <Input
                  id="reg-password"
                  name="new-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  minLength={6}
                  className="rounded-xl border-[#E5E7EB] focus:ring-2 focus:ring-[#6590FF] h-11 pr-11"
                  placeholder="Min 6 characters"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                  className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-[#9CA3AF] hover:text-[#4B5563] focus:outline-none focus-visible:text-[#6590FF] rounded-r-xl transition-colors"
                  data-testid="register-password-toggle"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#6590FF] hover:bg-[#4B7CFF] text-white font-semibold text-sm flex items-center justify-center gap-2 rounded-xl transition-colors disabled:opacity-50"
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          <p className="mt-6 text-sm text-[#4B5563] text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
            Already have an account?{' '}
            <Link to="/login" className="text-[#6590FF] font-semibold hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
